import type { ReactNode } from 'react';
import { useWhoami } from '../api/whoami';
import { ApiError } from '../api/problemDetails';
import { useAuthStatus } from './authStatus';
import { SigningInPage } from '../pages/SigningInPage';
import { SignInBlockedPage } from '../pages/SignInBlockedPage';
import { OopsPage } from '../pages/OopsPage';

/**
 * Reads the HTTP status off whatever the axios interceptors rejected with — an
 * `ApiError` for `problem+json` bodies, or the raw `AxiosError` for a bare body
 * (the backend's blocked-sign-in 401 has no body — TD-191).
 */
function statusOf(error: unknown): number {
  if (error instanceof ApiError) {
    return error.status;
  }
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number } }).response;
    return response?.status ?? 0;
  }
  return 0;
}

function traceIdOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.problem.traceId : undefined;
}

/**
 * Gates the authenticated shell on the `whoami` load. It resolves the
 * neutral→branded transition and the sign-in dead-ends that sit *between* a
 * successful MSAL sign-in and the shell:
 *
 *  - pending → the "signing you in" card (A2), neutral brand, no chrome.
 *  - success → the shell (`children`); customer branding snaps in here.
 *  - `401` → a blocked account (A3). The MSAL token is valid but the API
 *    refused the context, so this is a dead-end that must NOT fall back into
 *    the shell (loop-safety). If instead the token itself couldn't be renewed
 *    (`sessionExpired`), the global `SessionExpiredHost` owns the re-auth
 *    interstitial and this keeps a neutral backdrop under it.
 *  - other errors → `/oops`, fed a `traceId` when the API supplied one.
 *
 * The 403/404/oops guard pages and the guards themselves are foundation's — this
 * boundary composes them, it does not re-implement them.
 */
export function WhoamiBoundary({ children }: { children: ReactNode }) {
  const { isPending, isError, error } = useWhoami();
  const { sessionExpired } = useAuthStatus();

  if (isPending) {
    return <SigningInPage />;
  }

  if (isError) {
    if (statusOf(error) !== 401) {
      return <OopsPage traceId={traceIdOf(error)} />;
    }
    if (sessionExpired) {
      return <SigningInPage />;
    }
    return <SignInBlockedPage reason="generic" />;
  }

  return <>{children}</>;
}
